import React, { memo, useCallback, useMemo, useState } from 'react'
import { Form, Formik } from 'formik'
import { useSelector } from 'react-redux'
import type { PaymentConfig, SavePayoutSettingsPayload } from 'src/interfaces/payment'
import { getPayoutSettingsSaving } from 'src/selectors/paymentConfig'
import { ConfirmPricingModal } from '../ConfirmPricingModal'
import { buildPricingRecap } from '../pricingRecap'
import type { PricingChange } from '../pricingRecap'
import { PayoutSettingsFormContent } from './PayoutSettingsFormContent'
import { payoutSettingsValidator } from './PayoutSettingsForm.validator'
import { PAYOUT_SETTINGS_LABELS, toFormValues, toSavePayload } from './PayoutSettingsForm.mapper'
import type { PayoutSettingsFormValues } from './PayoutSettingsForm.interface'

interface Props {
  config: PaymentConfig
  partnerId: string
  customerName: string
  isReadOnly: boolean
  /** Resolves true when the save succeeded. See `handleConfirm` for why the result matters. */
  onSubmit(payload: SavePayoutSettingsPayload): Promise<boolean>
}

const PayoutSettingsFormComponent = ({
  config,
  partnerId,
  customerName,
  isReadOnly,
  onSubmit,
}: Props) => {
  const isSaving = useSelector(getPayoutSettingsSaving)
  const [pending, setPending] = useState<PayoutSettingsFormValues | null>(null)

  const initialValues = useMemo(() => {
    return toFormValues(config)
  }, [config])

  // Formik's onSubmit only fires on a valid form, so the modal never recaps values the save
  // would reject. Nothing is dispatched until the admin confirms.
  //
  // `async` rather than a plain function: Formik 2.4.9's `submitForm` bails on a non-promise
  // return and leaves `isSubmitting` true forever, so this resolves explicitly.
  const handleSubmit = useCallback(async (values: PayoutSettingsFormValues) => {
    setPending(values)
  }, [])

  const handleClose = useCallback(() => {
    setPending(null)
  }, [])

  // Awaited, and `pending` is cleared only on success — the same shape as this app's other
  // confirm-then-save modals (`CreateCustomerButton`, `InsuranceProductsPage`). Firing and
  // forgetting would close the modal in the same render that `isSaving` first turns true, so the
  // confirm button's spinner could never be seen, and a failed save would dismiss the dialog as
  // if it had worked. On a screen that moves real money, the dialog stays up until the save lands.
  const handleConfirm = useCallback(async () => {
    if (pending === null) {
      return
    }

    const saved = await onSubmit(toSavePayload(partnerId, pending))

    if (!saved) {
      return
    }

    setPending(null)
  }, [pending, partnerId, onSubmit])

  const changes: PricingChange[] = useMemo(() => {
    if (pending === null) {
      return []
    }

    return buildPricingRecap(initialValues, pending, PAYOUT_SETTINGS_LABELS)
  }, [initialValues, pending])

  return (
    <Formik<PayoutSettingsFormValues>
      initialValues={initialValues}
      validationSchema={payoutSettingsValidator}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <Form>
        <PayoutSettingsFormContent isReadOnly={isReadOnly} />
        <ConfirmPricingModal
          isOpen={pending !== null}
          customerName={customerName}
          changes={changes}
          isSaving={isSaving}
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      </Form>
    </Formik>
  )
}

export const PayoutSettingsForm = memo(PayoutSettingsFormComponent)
